import { useAutosave } from '@dvag/dfs-ui-blocks';
import {
  ResourceAndOperation,
  UpdateManyResources,
} from '@dvag/dfs-ui-blocks/hooks/useAutosaveHelper';
import { mandatoryFieldMessage } from 'form/util';
import { useMemo, useState } from 'react';
import { mixed, object, string } from 'yup';
import { ChartView } from '../component/FundsModal/types';
import { FondDto, FondMutationTransactionInputDto, Operation } from '../graphql/advisor/generates';
import { MAX_FUNDS_LENGTH } from '../utils/util';
import { useUpdateFunds } from './useUpdateFunds';

export interface SavedFundDto {
  id?: string;
  fondName: string;
  isin: string;
  orderPreviousItem: string | null;
  chartView: ChartView;
}

const mapOperation = (op: 'CREATE' | 'UPDATE' | 'DELETE'): Operation => {
  switch (op) {
    case 'CREATE':
      return Operation.Create;
    case 'UPDATE':
      return Operation.Update;
    case 'DELETE':
    default:
      return Operation.Delete;
  }
};

export function useFundsAutosave(closeModal: () => void, initial: SavedFundDto[] = []) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const schema = useMemo(
    () =>
      object().shape({
        id: string().nullable(),
        fondName: string().required(mandatoryFieldMessage),
        isin: string().required(mandatoryFieldMessage),
        chartView: mixed<ChartView>().required(mandatoryFieldMessage),
        orderPreviousItem: string().nullable(),
      }),
    [],
  );
  const { mutateAsync } = useUpdateFunds();

  const wrappedUpdateManyResources = ((list: ResourceAndOperation<FondDto>[]) => {
    const transactions: FondMutationTransactionInputDto[] = list.map(({ operation, data }) => ({
      operation: mapOperation(operation),
      data,
    }));

    return mutateAsync(transactions);
  }) as UpdateManyResources<FondDto>;

  const {
    append,
    remove,
    setValue,
    getAllResources,
    flushChanges,
    isSubmitting,
    triggerValidation,
    errors,
    register,
    getValues,
    isDirty,
  } = useAutosave<FondDto>({
    data: initial as FondDto[],
    resourceSchema: schema,
    updateManyResources: wrappedUpdateManyResources,
    autoAppendDefaultResource: false,
    debounceTime: -1,
    onIsDirtyChange: () => {},
    onSubmitSuccessfulChange: (isSuccessful) => {
      if (isSuccessful) {
        closeModal();
      }
    },
  });

  const funds = getAllResources();

  const disabledIsins = useMemo(
    () => funds.filter((_, i) => i !== selectedIndex).map((f) => f.isin),
    [funds, selectedIndex],
  );

  const appendFund = () => {
    if (
      funds.some((f) => {
        return !f.isin;
      }) ||
      funds.length >= MAX_FUNDS_LENGTH
    ) {
      return;
    }
    const lastFund = funds[funds.length - 1]?.isin ?? null;
    append({
      isin: '',
      fondName: '',
      chartView: ChartView.Comparison,
      orderPreviousItem: funds.length ? lastFund : null,
      id: undefined,
    });

    setSelectedIndex(funds.length);
  };

  const removeFund = (idx: number) => {
    if (funds.length === 1) {
      return;
    }
    if (getValues('chartView', idx + 1)) {
      setValue(`orderPreviousItem`, funds[idx - 1]?.isin ?? null, idx + 1);
    }
    remove(idx);

    if (idx === selectedIndex) {
      setSelectedIndex(0);
    }
  };

  const selectFund = (idx: number) => setSelectedIndex(idx);

  return {
    loading: isSubmitting,
    funds,
    selectedIndex,
    appendFund,
    removeFund,
    selectFund,
    setValue,
    getAllResources,
    flushChanges,
    disabledIsins,
    triggerValidation,
    errors,
    register,
    getValues,
    isDirty,
  };
}
